import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import {
  listSharedWithMe,
  removeSharedAccess
} from '../../../firebase/sharing';
import { Loader } from '../../App/components/Loader';

/**
 * "Shared with me" — sketches another user shared with the current user via a capability link. Each
 * row deep-links back into the sketch WITH its token, so the recipient never needs to keep the URL.
 */
const SharedList = () => {
  const { t } = useTranslation();
  const userId = useSelector((state) => state.user.id);
  const authHydrated = useSelector((state) => state.user.authHydrated);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (userId && authHydrated) {
      setLoading(true);
      listSharedWithMe()
        .then((rows) => {
          if (!cancelled) setItems(rows);
        })
        .catch(() => {
          if (!cancelled) setItems([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [userId, authHydrated]);

  const handleRemove = async (projectId) => {
    setItems((prev) => prev.filter((it) => it.projectId !== projectId));
    try {
      await removeSharedAccess(projectId);
    } catch (e) {
      // best-effort; it'll reappear on next load if the delete failed
    }
  };

  const resolving = !authHydrated || loading;

  return (
    <article className="sketches-table-container">
      <Helmet>
        <title>
          {t('SharedList.Title', { defaultValue: 'Shared with me' })}
        </title>
      </Helmet>
      {resolving && <Loader />}
      {!resolving && items.length === 0 && (
        <p className="sketches-table__empty">
          {t('SharedList.Empty', {
            defaultValue: 'No sketches have been shared with you yet.'
          })}
        </p>
      )}
      {!resolving && items.length > 0 && (
        <table className="sketches-table" summary="shared sketches">
          <thead>
            <tr>
              <th>{t('SharedList.HeaderName', { defaultValue: 'Name' })}</th>
              <th>{t('SharedList.HeaderOwner', { defaultValue: 'Owner' })}</th>
              <th>
                {t('SharedList.HeaderAccess', { defaultValue: 'Access' })}
              </th>
              <th aria-label="remove" scope="col" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr className="sketches-table__row" key={item.projectId}>
                <th scope="row">
                  <Link
                    to={`/${item.ownerUsername}/sketches/${item.projectId}?token=${item.token}`}
                  >
                    {item.name}
                  </Link>
                </th>
                <td>{item.ownerUsername}</td>
                <td>
                  {item.capability === 'edit'
                    ? t('SharedList.CanEdit', { defaultValue: 'Can edit' })
                    : t('SharedList.ViewOnly', { defaultValue: 'View only' })}
                </td>
                <td>
                  <button
                    type="button"
                    className="sketch-list__dropdown-button"
                    onClick={() => handleRemove(item.projectId)}
                  >
                    {t('SharedList.Remove', { defaultValue: 'Remove' })}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </article>
  );
};

export default SharedList;
